/**
 * LLM 模型选择器（共享组件）
 *
 * 在业务功能配置页面使用。下拉列出所有 LlmModel，选中后把 modelId（uuid）
 * 写入业务字段（如 DirectorSystemConfig.shotSplitLlmModelId）。
 *
 * 兼容期：当业务字段值不是已知 modelId 时（如老配置里存的是 "deepseek-chat" 字符串），
 * 显示一条警告"识别到老配置，请重新选择模型"。
 */

import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { llmPlatformAPI, type LlmModel } from '../../../baselines/llm/llmPlatformAPI';

interface LlmModelPickerProps {
  value: string | null | undefined;
  onChange: (modelId: string) => void;
  /** 是否允许"清空"（业务功能要求必填则禁用） */
  allowEmpty?: boolean;
  disabled?: boolean;
}

export default function LlmModelPicker({
  value,
  onChange,
  allowEmpty = false,
  disabled,
}: LlmModelPickerProps) {
  const [models, setModels] = useState<LlmModel[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    llmPlatformAPI
      .listModels()
      .then((res) => {
        if (!cancelled) setModels(res.data);
      })
      .catch(() => {
        // 失败时维持空列表；外部 toast 已由其他位置处理
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isLegacyValue = useMemo(() => {
    if (!value) return false;
    const looksLikeUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
    if (!looksLikeUuid) return true;
    // 即便是 uuid，但不在已加载列表里，也视为"无效"
    if (models.length === 0) return false;
    return !models.find((m) => m.id === value);
  }, [value, models]);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <select
          className="input flex-1"
          value={value && !isLegacyValue ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled || loading}
        >
          {allowEmpty && <option value="">（未选择）</option>}
          {(!allowEmpty && (!value || isLegacyValue)) && (
            <option value="">请选择模型</option>
          )}
          {models.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name} （{m.modelIdString}）
              {!m.isActive ? '【已禁用】' : ''}
              {m.channel && !m.channel.isActive ? '【渠道禁用】' : ''}
            </option>
          ))}
        </select>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-light-muted" />}
      </div>

      {isLegacyValue && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
          检测到老配置：当前值 <code className="font-mono">{value}</code>{' '}
          不是 LLM 平台中的模型 ID。运行时会自动走老路径（直连 apiUrl/apiKey）。
          建议在「LLM 平台 → 模型」中创建对应模型并在此重新选择。
        </div>
      )}

      {models.length === 0 && !loading && (
        <div className="text-xs text-light-muted">
          暂无可选模型。请前往「AI 功能 → LLM 平台」创建渠道与模型后，回到此处刷新。
        </div>
      )}
    </div>
  );
}
